import { useEffect, useRef, useState } from 'react'

// ── Animated number — rolls to its new value over `duration` ms ─────────
// <CountTicker value={26} duration={500} />
// Cheap: uses requestAnimationFrame, no library. Honors prefers-reduced-motion.

export function CountTicker({ value, duration = 500, format = (n) => n, from }) {
  // `from` (optional) seeds the starting number so the ticker animates on FIRST mount
  // (e.g. 0 → score). Omit it and the ticker starts at `value` and only rolls on change.
  const [display, setDisplay] = useState(from ?? value)
  const fromRef = useRef(from ?? value)
  const frameRef = useRef(null)

  useEffect(() => {
    const reduce = typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (reduce || duration <= 0) {
      setDisplay(value)
      fromRef.current = value
      return
    }
    const from = fromRef.current
    const to = value
    if (from === to) return
    const startTime = performance.now()
    const tick = (now) => {
      const t = Math.min(1, (now - startTime) / duration)
      const eased = 1 - Math.pow(1 - t, 3) // ease-out cubic
      setDisplay(Math.round(from + (to - from) * eased))
      if (t < 1) {
        frameRef.current = requestAnimationFrame(tick)
      } else {
        fromRef.current = to
      }
    }
    frameRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frameRef.current)
  }, [value, duration])

  return format(display)
}
